<template>
  <div class="chat-search-panel" v-if="visible">
    <div class="search-header">
      <el-input v-model="keyword" placeholder="搜索聊天记录" prefix-icon="Search" clearable
        @keyup.enter="doSearch" size="small" ref="searchInput" />
      <el-select v-if="isGroup && members.length > 0" v-model="senderId" placeholder="按成员筛选"
        clearable size="small" class="member-filter" @change="doSearch">
        <el-option v-for="m in members" :key="m.userId" :label="m.showNickName || m.nickName"
          :value="m.userId" />
      </el-select>
      <el-button size="small" type="primary" @click="doSearch" :loading="loading">搜索</el-button>
      <el-button size="small" @click="$emit('close')">关闭</el-button>
    </div>
    <div class="search-results" v-if="results.length > 0">
      <div class="result-item" v-for="item in results" :key="item.id" @click="$emit('locate', item)">
        <div class="result-sender">{{ item.sendNickName || ('用户' + item.sendId) }}</div>
        <div class="result-content" v-html="highlightKeyword(item.content)"></div>
        <div class="result-time">{{ formatTime(item.sendTime) }}</div>
      </div>
      <div class="load-more" v-if="hasMore" @click="loadMore">加载更多</div>
    </div>
    <div class="search-empty" v-else-if="searched && !loading">
      <span>未找到相关消息</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatSearchPanel',
  props: {
    visible: { type: Boolean, default: false },
    isGroup: { type: Boolean, default: false },
    chatInfo: { type: Object, default: () => ({}) },
    members: { type: Array, default: () => [] }
  },
  data() {
    return {
      keyword: '',
      senderId: null,
      results: [],
      loading: false,
      searched: false,
      page: 1,
      hasMore: false
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus();
        });
      } else {
        this.reset();
      }
    }
  },
  methods: {
    reset() {
      this.keyword = '';
      this.senderId = null;
      this.results = [];
      this.searched = false;
      this.page = 1;
      this.hasMore = false;
    },
    async doSearch() {
      if (!this.keyword.trim()) return;
      this.page = 1;
      this.results = [];
      await this.fetchResults();
    },
    async loadMore() {
      this.page++;
      await this.fetchResults();
    },
    async fetchResults() {
      this.loading = true;
      this.searched = true;
      try {
        let url, params;
        if (this.isGroup) {
          url = '/message/group/search';
          params = {
            keyword: this.keyword.trim(),
            groupId: this.chatInfo.id,
            senderId: this.senderId || undefined,
            page: this.page,
            size: 20
          };
        } else {
          url = '/message/private/search';
          params = {
            keyword: this.keyword.trim(),
            friendId: this.chatInfo.id,
            page: this.page,
            size: 20
          };
        }
        const res = await this.$http.post(url, params);
        if (res.code === 200) {
          const list = res.data || [];
          if (this.page === 1) {
            this.results = list;
          } else {
            this.results = this.results.concat(list);
          }
          this.hasMore = list.length >= 20;
        }
      } catch (e) {
        console.error('搜索失败', e);
      } finally {
        this.loading = false;
      }
    },
    highlightKeyword(content) {
      if (!content || !this.keyword) return content || '';
      const escaped = this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return content.replace(new RegExp(escaped, 'gi'), '<mark>$&</mark>');
    },
    formatTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      const pad = (n) => String(n).padStart(2, '0');
      if (isToday) {
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
      return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  }
}
</script>

<style scoped lang="scss">
.chat-search-panel {
  border-bottom: 1px solid var(--im-border-color);
  padding: 10px 15px;
  background: var(--im-background);

  .search-header {
    display: flex;
    align-items: center;
    gap: 8px;

    .el-input {
      flex: 1;
    }

    .member-filter {
      width: 140px;
    }
  }

  .search-results {
    max-height: 300px;
    overflow-y: auto;
    margin-top: 10px;

    .result-item {
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
      border-bottom: 1px solid var(--im-border-color-light);

      &:hover {
        background: var(--im-bg-hover);
      }

      .result-sender {
        font-size: 12px;
        color: var(--im-text-color-secondary);
        margin-bottom: 2px;
      }

      .result-content {
        font-size: 13px;
        color: var(--im-text-color);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        :deep(mark) {
          background: #ffeb3b;
          color: inherit;
          padding: 0 1px;
          border-radius: 2px;
        }
      }

      .result-time {
        font-size: 11px;
        color: var(--im-text-color-lighter);
        margin-top: 2px;
      }
    }

    .load-more {
      text-align: center;
      padding: 8px;
      color: var(--im-color-primary);
      cursor: pointer;
      font-size: 13px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .search-empty {
    text-align: center;
    padding: 20px;
    color: var(--im-text-color-lighter);
    font-size: 13px;
  }
}
</style>
